import { AtlasCore } from '@crossnokaye/typescript-sdk/core';
import { RequestOptions } from '@crossnokaye/typescript-sdk/lib/sdks';
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  SDKError,
  SDKValidationError,
  UnexpectedClientError,
} from '@crossnokaye/typescript-sdk/models/errors';
import { Result, unwrapAsync } from '@crossnokaye/typescript-sdk/types/fp';

import { BACKEND_URL } from '@/constants';

type AtlasFuncWithRequest<TRequest, TResponse> = (
  core: AtlasCore,
  request: TRequest,
  options?: RequestOptions
) => Promise<
  Result<
    TResponse,
    | SDKError
    | SDKValidationError
    | UnexpectedClientError
    | InvalidRequestError
    | RequestAbortedError
    | RequestTimeoutError
    | ConnectionError
  >
>;

type AtlasFuncWithoutRequest<TResponse> = (
  core: AtlasCore,
  options?: RequestOptions
) => Promise<
  Result<
    TResponse,
    | SDKError
    | SDKValidationError
    | UnexpectedClientError
    | InvalidRequestError
    | RequestAbortedError
    | RequestTimeoutError
    | ConnectionError
  >
>;

interface AtlasClient<TRequest, TResponse> {
  atlasFunc: AtlasFuncWithRequest<TRequest, TResponse> | AtlasFuncWithoutRequest<TResponse>;
  request?: TRequest;
  options?: RequestOptions;
  getAccessToken: () => Promise<string>;
}

export const atlasClient = async <TRequest, TResponse>({
  atlasFunc,
  request,
  options,
  getAccessToken,
}: AtlasClient<TRequest, TResponse>) => {
  // Instantiate new Atlas Core to ensure we have a fresh instance of the core object
  const authHeader = `Bearer ${await getAccessToken()}`;
  const atlasCore = new AtlasCore({
    oauth2HeaderAuthorization: authHeader,
    serverURL: BACKEND_URL.toString(),
  });

  if (request) {
    return unwrapAsync(
      (atlasFunc as AtlasFuncWithRequest<TRequest, TResponse>)(atlasCore, request, options)
    );
  }
  return unwrapAsync((atlasFunc as AtlasFuncWithoutRequest<TResponse>)(atlasCore, options));
};
